<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert as wgptUseAlert } from 'dashboard/composables/wgpt';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import CreateWorkflowForm from './CreateWorkflowForm.vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const uiFlags = getters['wgptBotWorkflows/getUIFlags'];

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate } = formRef.value;

    const data = await validate();
    if (!data) return;

    const workflow = {
      analyzer_prompt: '',
      rag_prompt: '',
      ...data,
    };
    await store.dispatch('wgptBotWorkflows/create', {
      ...workflow,
      botId: route.params.botId,
    });

    useAlert(t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.CREATE.API.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.CREATE.API.ERROR_MESSAGE');
    wgptUseAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.CREATE.TITLE')"
    :confirm-button-label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.CREATE.SAVE')"
    :cancel-button-label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.CREATE.CANCEL')"
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    width="2xl"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <SnackbarContainer />
    <div
      v-if="isOpen"
      class="max-h-[calc(100vh-14rem)] overflow-y-auto -mx-6 px-6"
    >
      <CreateWorkflowForm ref="formRef" @submit="handleSubmit" />
    </div>
  </Dialog>
</template>
